// Links between the entries, worked out once per board. The requirement is the hub: goals, criteria and
// features all hang on requirements, so "what belongs to X" always goes through X's requirements. A Messgröße
// hangs on its goal and shares the goal's chain.
import { PREFIX, type Board, type Kind } from "../lib/types.ts";

export type Key = `${Kind}:${number}`;
export const key = (kind: Kind, id: number): Key => `${kind}:${id}`;
export const parseKey = (k: Key): [Kind, number] => {
  const [kind, id] = k.split(":");
  return [kind as Kind, Number(id)];
};

export interface Graph {
  label: (k: Key) => string;
  title: (k: Key) => string;
  related: (k: Key) => Set<Key>;
  goalsOfReq: Map<number, number[]>;
  reqsOfGoal: Map<number, number[]>;
  featuresOfReq: Map<number, number[]>;
  reqsOfFeature: Map<number, number[]>;
  criteriaOfReq: Map<number, number[]>;
  /** Items of each main requirement, in table order. */
  itemsOfMain: Map<number, number[]>;
  measuresOfGoal: Map<number, number[]>;
  /** Open questions per linked entry. */
  openQuestions: Map<Key, number>;
  /** Questions linked to an entry, in either direction. */
  questionsOf: (k: Key) => number[];
}

function push(m: Map<number, number[]>, k: number, v: number) {
  const list = m.get(k);
  if (list) list.push(v);
  else m.set(k, [v]);
}

export function buildGraph(b: Board): Graph {
  const goalsOfReq = new Map<number, number[]>();
  const reqsOfGoal = new Map<number, number[]>();
  const featuresOfReq = new Map<number, number[]>();
  const reqsOfFeature = new Map<number, number[]>();
  const criteriaOfReq = new Map<number, number[]>();
  for (const [g, r] of b.goalRequirements) {
    push(goalsOfReq, r, g);
    push(reqsOfGoal, g, r);
  }
  for (const [f, r] of b.featureRequirements) {
    push(featuresOfReq, r, f);
    push(reqsOfFeature, f, r);
  }
  for (const c of b.criteria) push(criteriaOfReq, c.requirementId, c.id);
  const itemsOfMain = new Map<number, number[]>();
  const parentOf = new Map<number, number>();
  for (const r of b.requirements)
    if (r.parentId !== null) {
      push(itemsOfMain, r.parentId, r.id);
      parentOf.set(r.id, r.parentId);
    }
  const reqNum = new Map(b.requirements.map((r) => [r.id, r.num]));
  const measuresOfGoal = new Map<number, number[]>();
  for (const m of b.measures) push(measuresOfGoal, m.goalId, m.id);

  const names = new Map<Key, { num: number; title: string }>();
  const add = (kind: Kind, list: { id: number; num: number; title: string }[]) =>
    list.forEach((e) => names.set(key(kind, e.id), e));
  add("goal", b.goals);
  add("measure", b.measures);
  add("requirement", b.requirements);
  add("criterion", b.criteria);
  add("feature", b.features);
  add("question", b.questions);

  const questionLinks = new Map<Key, Key[]>();
  const questionsOf = new Map<Key, Key[]>();
  const openQuestions = new Map<Key, number>();
  for (const q of b.questions) {
    const qk = key("question", q.id);
    const targets = q.links.map((l) => key(l.kind, l.targetId)).filter((t) => names.has(t));
    questionLinks.set(qk, targets);
    for (const t of targets) {
      questionsOf.set(t, [...(questionsOf.get(t) ?? []), qk]);
      if (q.status === "offen") openQuestions.set(t, (openQuestions.get(t) ?? 0) + 1);
    }
  }

  const reqsOf = (k: Key): number[] => {
    const [kind, id] = parseKey(k);
    if (kind === "goal") return reqsOfGoal.get(id) ?? [];
    if (kind === "measure") {
      const m = b.measures.find((m) => m.id === id);
      return m ? (reqsOfGoal.get(m.goalId) ?? []) : [];
    }
    if (kind === "requirement") return parentOf.has(id) ? [id] : (itemsOfMain.get(id) ?? []);
    if (kind === "criterion") {
      const c = b.criteria.find((c) => c.id === id);
      return c ? [c.requirementId] : [];
    }
    if (kind === "feature") return reqsOfFeature.get(id) ?? [];
    return [];
  };

  const related = (k: Key): Set<Key> => {
    const [kind] = parseKey(k);
    const out = new Set<Key>();
    if (kind === "question") {
      // links between questions count both ways, wherever they are stored
      for (const t of [...(questionLinks.get(k) ?? []), ...(questionsOf.get(k) ?? [])]) out.add(t);
      return out;
    }
    const addGoal = (g: number) => {
      out.add(key("goal", g));
      for (const m of measuresOfGoal.get(g) ?? []) out.add(key("measure", m));
    };
    if (kind === "goal") for (const m of measuresOfGoal.get(parseKey(k)[1]) ?? []) out.add(key("measure", m));
    if (kind === "measure") {
      const m = b.measures.find((m) => m.id === parseKey(k)[1]);
      if (m) out.add(key("goal", m.goalId));
    }
    for (const r of reqsOf(k)) {
      out.add(key("requirement", r));
      // other entries of the start's own kind are siblings, not part of its chain
      if (kind !== "goal" && kind !== "measure") for (const g of goalsOfReq.get(r) ?? []) addGoal(g);
      if (kind !== "criterion") for (const c of criteriaOfReq.get(r) ?? []) out.add(key("criterion", c));
      if (kind !== "feature") for (const f of featuresOfReq.get(r) ?? []) out.add(key("feature", f));
    }
    for (const q of questionsOf.get(k) ?? []) out.add(q);
    // a main requirement's items belong to its chain; an item's main requirement lights up with it
    for (const t of [...out]) {
      const [tk, tid] = parseKey(t);
      if (tk === "requirement" && parentOf.has(tid)) out.add(key("requirement", parentOf.get(tid)!));
    }
    if (kind === "requirement" && parentOf.has(parseKey(k)[1])) out.add(key("requirement", parentOf.get(parseKey(k)[1])!));
    out.delete(k);
    return out;
  };

  return {
    label: (k) => {
      const [kind, id] = parseKey(k);
      // items of a main requirement: A3.2
      if (kind === "requirement" && parentOf.has(id)) return `A${reqNum.get(parentOf.get(id)!)}.${reqNum.get(id)}`;
      return `${PREFIX[kind]}${names.get(k)?.num ?? "?"}`;
    },
    title: (k) => names.get(k)?.title ?? "",
    related,
    goalsOfReq,
    reqsOfGoal,
    featuresOfReq,
    reqsOfFeature,
    criteriaOfReq,
    itemsOfMain,
    measuresOfGoal,
    openQuestions,
    questionsOf: (k) => {
      const out = new Set((questionsOf.get(k) ?? []).map((q) => parseKey(q)[1]));
      if (parseKey(k)[0] === "question")
        for (const t of questionLinks.get(k) ?? []) if (parseKey(t)[0] === "question") out.add(parseKey(t)[1]);
      return [...out];
    },
  };
}
